import { FC, Fragment, useMemo, useState } from 'react';

import { isEmpty, isNonNullish, isNullish } from 'remeda';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';

import css from './EditTasksDialog.module.css';

import { tasksService } from 'v2/api/tasksService';
import { tournamentsService } from 'v2/api/tournamentsService';
import { userService } from 'v2/api/userService';
import { EmptyPlaceholder } from 'v2/components/emptyPlaceholder/EmptyPlaceholder';
import { Loader } from 'v2/components/loader/Loader';
import { DailyTaskModel } from 'v2/types/TaskModel';
import { UserModel } from 'v2/types/UserModel';
import { handleHttpError, toggleArrayItem } from 'v2/utils';

interface EditTasksDialogProps {
  user: UserModel;
  isOpen: boolean;
  onClose: VoidFunction;
  onRevalidate: VoidFunction;
}

export const EditTasksDialog: FC<EditTasksDialogProps> = ({ user, isOpen, onClose, onRevalidate }) => {
  const [selectedTasksIds, setSelectedTasksIds] = useState<string[]>(() =>
    user.completedBaseTaskIds.concat(user.completedEpicTaskIds)
  );

  const { data: dailyTasks, isLoading: isLoadingDailyTasks } = useSWR(
    isOpen && 'getAllDailyTasks',
    tasksService.getAllDailyTasks
  );

  const { data: activeTournament, isLoading: isLoadingActiveTournament } = useSWR(
    isOpen && 'getActiveTournament',
    tournamentsService.getActiveTournament
  );

  const { trigger } = useSWRMutation(['updateUser', user.id], userService.updateUser, {
    onSuccess: (user) => {
      setSelectedTasksIds(user.completedBaseTaskIds.concat(user.completedEpicTaskIds));
      onRevalidate();
      toast.success('Daily task updated successfully');
    },
    onError: handleHttpError,
  });

  const activeTasks = useMemo(() => {
    if (isNullish(dailyTasks) || isNullish(activeTournament)) {
      return null;
    }

    return dailyTasks.filter((task) => task.tournamentId === activeTournament.id);
  }, [activeTournament, dailyTasks]);

  const handleChange = (task: DailyTaskModel) => () => {
    trigger(
      task.isSpecial
        ? { completedEpicTaskIds: toggleArrayItem(user.completedEpicTaskIds, task.id) }
        : { completedBaseTaskIds: toggleArrayItem(user.completedBaseTaskIds, task.id) }
    );
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit User Daily Tasks</DialogTitle>

      <DialogContent className={css.editTasksDialogContent}>
        {isLoadingDailyTasks || isLoadingActiveTournament ? (
          <Loader />
        ) : (
          isNonNullish(activeTasks) && (
            <Fragment>
              {isEmpty(activeTasks) ? (
                <EmptyPlaceholder />
              ) : (
                <FormControl>
                  <InputLabel>Daily Tasks</InputLabel>
                  <Select
                    multiple
                    value={selectedTasksIds}
                    input={<OutlinedInput label="Daily Tasks" />}
                    renderValue={(selected) =>
                      selected.map((id) => {
                        const label = activeTasks?.find((task) => task.id === id)?.description;

                        return <Chip key={id} label={label ?? ''} size="small" />;
                      })
                    }
                    fullWidth
                  >
                    {activeTasks.map((task) => (
                      <MenuItem key={task.id} value={task.id} onClick={handleChange(task)}>
                        <Checkbox checked={selectedTasksIds.includes(task.id)} />
                        {task.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Fragment>
          )
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="error">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
